import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  EnvelopeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

import { Modal, ModalTitle } from 'components/Modal';
import InfoTooltip from 'components/InfoTooltip';
import {
  EASYPAY_ONBOARDING_URL,
  EASYPAY_LOGIN_URL,
  EASYPAY_SITE_URL,
} from 'utils/easypayPayout';
import {
  T2B_FEE_RATE,
  VAT_RATE,
  computeBookingPricing,
} from 'utils/bookingPricing';

/**
 * The Easypay story, told in the two places a promoter needs it: at signup
 * (T2B-423) and later from their dashboard if they skipped it.
 *
 * Everything here is copy — no gating logic. Whether a promoter is connected is
 * `useEasypayGate`'s answer, and the API's; this module only explains what
 * connecting involves and what happens once they are.
 */

/* ─────────────────────── copy with the Easypay link ──────────────────────── */

/**
 * Body copy in which the word "Easypay" is a link to easypay.pt.
 *
 * The translations mark it up as `<a>Easypay</a>` and this splits on that, the
 * same pattern as the `<em>` in the registration submit bar. A real markup
 * parser (or <Trans>) would be more than this needs: one inline element, one
 * destination, and translators who must be free to move the word to wherever
 * their sentence puts it.
 *
 * A promoter is being asked to hand their bank details and identity documents
 * to a company they may never have heard of. The link is there so they can go
 * and check who that company is without leaving the flow to search for it.
 */
export const EasypayCopy = ({
  i18nKey,
  fallback,
  className = '',
  // The dashboard card hands its copy to a header that already wraps it in a
  // <p>, and a <p> inside a <p> is closed by the parser before it starts.
  as: Tag = 'p',
}) => {
  const { t } = useTranslation();

  return (
    <Tag className={className}>
      {t(i18nKey, fallback)
        .split(/<a>(.*?)<\/a>/)
        .map((part, i) =>
          i % 2 === 1 ? (
            <a
              key={i}
              href={EASYPAY_SITE_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="font-semibold text-ink underline underline-offset-2 transition hover:text-brand-deep"
            >
              {part}
            </a>
          ) : (
            part
          )
        )}
    </Tag>
  );
};

/* ───────────────────────── what it takes (the 4 steps) ───────────────────── */

/**
 * Steps 1–2 are the promoter's to do and are drawn in brand yellow; 3–4 happen
 * without them and stay grey. That split is the point of the graphic — it says
 * "eight minutes of your time, then you wait", which is the honest shape of
 * Easypay onboarding and the reason the wait doesn't read as a dead end.
 */
const STEPS = [
  { key: 'account', theirs: true, meta: 'time' },
  { key: 'details', theirs: true, meta: 'time' },
  { key: 'kyc', theirs: false, meta: 'time' },
  { key: 'ready', theirs: false, meta: 'mail' },
];

export const PayoutJourney = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      {/* The hint rides on the label rather than sitting under it: the two
          yellow numbers already say "these ones are yours" visually, and this
          is the caption for them, not a line of its own. Lighter weight so the
          label still reads as a heading. */}
      <div className="font-display text-[11.5px] font-bold uppercase tracking-[0.12em] text-ink-2">
        {t('easypay.steps_label', 'What it takes')}
        <span className="font-semibold text-muted">
          {' \u2014 '}
          {t('easypay.steps_label_hint', 'do the first two steps now')}
        </span>
      </div>

      {/* The connector rail sits behind the numbers on wide screens; on narrow
          ones the cards stack and the rail would point nowhere, so it's gone. */}
      <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, i) => (
          <li key={step.key} className="relative flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <span
                className={[
                  'grid h-9 w-9 shrink-0 place-items-center rounded-full text-[13px] font-extrabold',
                  step.theirs
                    ? 'bg-brand text-ink'
                    : 'bg-paper-2 text-muted-2',
                ].join(' ')}
              >
                {i + 1}
              </span>
              {i < STEPS.length - 1 && (
                <span
                  aria-hidden
                  className="hidden h-px flex-1 bg-line-strong lg:block"
                />
              )}
            </div>

            <div className="flex h-full flex-col gap-1.5 rounded-[12px] bg-paper-2/60 p-4">
              <h4 className="m-0 text-[14px] font-extrabold leading-snug text-ink-strong">
                {t(`easypay.step_${step.key}_title`)}
              </h4>
              <p className="m-0 flex-1 text-[12.5px] leading-[1.45] text-muted">
                {t(`easypay.step_${step.key}_body`)}
              </p>
              <span className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11.5px] font-semibold text-ink-2">
                {step.meta === 'mail' ? (
                  <EnvelopeIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
                ) : (
                  <ClockIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
                )}
                {t(`easypay.step_${step.key}_meta`)}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ──────────────────────── connect button + reassurance ───────────────────── */

/**
 * `showAccessLink` adds a second, quieter door to an EXISTING Easypay account.
 *
 * Off by default, because the two places this renders have different people in
 * front of them. At registration nobody has signed up yet — that's the whole
 * point of the step — so offering "Go to Easypay" there is an invitation to
 * log into an account that doesn't exist. On the dashboard the opposite is
 * true: a promoter who is back on this card has almost certainly been through
 * Easypay's form already and wants to check how far KYC has got.
 */
export const EasypayConnectButton = ({
  label,
  className = '',
  showAccessLink = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex flex-wrap items-center gap-2.5">
        <a
          href={EASYPAY_ONBOARDING_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex w-fit items-center gap-2 rounded-[10px] bg-brand px-5 py-3.5 text-[15px] font-semibold text-ink transition hover:bg-brand-hover"
        >
          {label || t('easypay.connect_cta', 'Create my Easypay account')}
          <ArrowTopRightOnSquareIcon className="h-4 w-4" strokeWidth={2} />
        </a>

        {showAccessLink && (
          <a
            href={EASYPAY_LOGIN_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex w-fit items-center gap-2 rounded-[10px] border border-line-strong px-5 py-3.5 text-[15px] font-semibold text-ink transition hover:bg-paper-2"
          >
            {t('easypay.open_cta', 'Go to Easypay')}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" strokeWidth={2} />
          </a>
        )}
      </div>

      {/* T2B-423 asks for this line right under the button: the promoter is
          about to leave for a days-long KYC they can't watch, so say plainly
          that they don't have to come back and check. */}
      <p className="m-0 text-[13px] leading-[1.5] text-ink-2">
        {t(
          'easypay.connect_reassurance',
          "We'll let you know as soon as you're connected."
        )}
      </p>

      <p className="m-0 inline-flex items-center gap-1.5 text-[12.5px] leading-[1.5] text-muted">
        <LockClosedIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
        {t(
          'easypay.connect_lock_note',
          'Event creation stays locked until your account is connected.'
        )}
      </p>
    </div>
  );
};

/* ──────────────────────── the locked-button explainer ────────────────────── */

/**
 * The "i" beside a disabled "Create event" button.
 *
 * The buttons are genuinely `disabled` now, which removes the one place the
 * explanation used to live: a disabled control takes no focus and fires no
 * hover, so its `title` never appears and a click can't carry the promoter
 * anywhere. Hence a separate affordance that is itself focusable, and a panel
 * the pointer can reach — the link inside it is the point.
 *
 * It links straight to Easypay's signup form rather than to there2b's own
 * Payment Details page: creating the account is the step that actually unblocks
 * anything, and the header notice already offers the slower route.
 */
export const EasypayLockedHint = ({
  // Defaults suit the toolbars: these buttons live at the right-hand end of
  // one, and the site header's would open off the top of the viewport. The
  // homepage's promoter card overrides both — its CTA sits at the bottom of a
  // dark panel, so the tooltip opens upward and the icon needs a light tone.
  placement = 'bottom',
  align = 'right',
  tone = 'default',
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <InfoTooltip
      interactive
      placement={placement}
      align={align}
      tone={tone}
      label={t('easypay.locked_tooltip')}
      className={className}
    >
      <span className="block">{t('easypay.locked_explainer')}</span>
      <a
        href={EASYPAY_ONBOARDING_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-1.5 inline-flex items-center gap-1 font-bold text-brand underline underline-offset-2"
      >
        {t('easypay.connect_cta')}
        <ArrowTopRightOnSquareIcon className="h-3 w-3" strokeWidth={2.2} />
      </a>
    </InfoTooltip>
  );
};

/* ───────────────────────────── how you get paid ──────────────────────────── */

/**
 * The six steps from "you create an event" to "the money is in your bank".
 * The last two are drawn in green because they're the ones that answer the
 * question promoters actually ask — when do I see it, and when do I have it.
 */
const PAID_STEPS = [
  { key: 'create', paid: false },
  { key: 'request', paid: false },
  { key: 'accept', paid: false },
  { key: 'pay', paid: false },
  { key: 'visible', paid: true },
  { key: 'transfer', paid: true },
];

/**
 * The same arithmetic the booking flow charges by, not a hand-written table:
 * a promoter who checks this against a real invoice has to find the same
 * numbers, and the fee has moved once already (T2B_FEE_CAP_FROM).
 *
 * €100 is chosen because it is in the clean 5% band — the €1 floor and €50 cap
 * would both turn the headline percentage into a lie for the example's own
 * figures. The split is per-party VAT: the promoter's share is their price plus
 * VAT on it, there2b's is the fee plus VAT on that, and the two add up to what
 * the brand paid.
 */
const EXAMPLE_PRICE = 100;

const exampleSplit = () => {
  const { t2bFee, total } = computeBookingPricing(EXAMPLE_PRICE);
  const toPromoter = Math.round(EXAMPLE_PRICE * (1 + VAT_RATE) * 100) / 100;

  return {
    price: EXAMPLE_PRICE,
    fee: t2bFee,
    total,
    toPromoter,
    // Derived by subtraction so the column always adds up on screen, whatever
    // rounding does to the two halves.
    toThere2b: Math.round((total - toPromoter) * 100) / 100,
  };
};

// Follows the UI language — an English reader shown "100,00 €" has to stop and
// work out that it is the same number they'll see at checkout.
//
// Widened to a full locale first, as the Financials tab does: i18n's language
// is the bare "pt", and Intl reads that as Brazilian ("€ 100,00"), not the
// "100,00 €" a Portuguese promoter reads everywhere else on the site.
const money = (amount, lang) =>
  new Intl.NumberFormat(lang === 'pt' ? 'pt-PT' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(amount || 0));

const ExampleRow = ({ label, note, amount, highlight = false }) => (
  <div
    className={[
      'flex items-baseline justify-between gap-4 py-2.5',
      highlight ? 'text-good' : 'text-ink-strong',
    ].join(' ')}
  >
    <span className="min-w-0 text-[13px] leading-snug">
      <span className="font-bold">{label}</span>
      {note && (
        <span className={highlight ? 'text-good/80' : 'text-muted'}>
          {' \u00b7 '}
          {note}
        </span>
      )}
    </span>
    <span className="shrink-0 text-[14.5px] font-extrabold tabular-nums">
      {amount}
    </span>
  </div>
);

/**
 * The example is the half of this modal promoters actually read: the six steps
 * say when the money moves, and it says how much. Hence the wider panel — at
 * `sm` the "Brand pays €129.15" rows wrapped, which is exactly the wrong place
 * for a number to break.
 */
const PaidExample = () => {
  const { t, i18n } = useTranslation();
  const ex = exampleSplit();
  const vatLabel = t('easypay.how_paid_example_vat', 'VAT');
  const fmt = (amount) => money(amount, i18n?.language);

  return (
    <div className="mt-6 rounded-[14px] bg-paper-2/70 p-5">
      <div className="font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
        {t('easypay.how_paid_example_label', 'Example')}
      </div>

      <p className="m-0 mt-2 text-[13px] leading-[1.5] text-ink-2">
        {t('easypay.how_paid_example_intro', {
          price: fmt(ex.price),
          rate: `${Math.round(T2B_FEE_RATE * 100)}%`,
          vat: vatLabel,
        })}
      </p>

      <div className="mt-3 divide-y divide-line">
        <ExampleRow
          label={t('easypay.how_paid_example_brand', 'Brand pays')}
          amount={fmt(ex.total)}
        />
        <ExampleRow
          label={t('easypay.how_paid_example_you', 'To you')}
          note={`${fmt(ex.price)} + ${vatLabel}`}
          amount={fmt(ex.toPromoter)}
          highlight
        />
        <ExampleRow
          label={t('easypay.how_paid_example_t2b', 'To there2b')}
          note={`${fmt(ex.fee)} + ${vatLabel}`}
          amount={fmt(ex.toThere2b)}
        />
      </div>
    </div>
  );
};

export const HowYouGetPaidModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="px-6 pb-3 pt-8 sm:px-9">
        <ModalTitle size="sm">
          {t('easypay.how_paid_title', 'How you get paid')}
        </ModalTitle>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-6 pb-8 sm:px-9">
        <ol className="m-0 flex list-none flex-col gap-4 p-0">
          {PAID_STEPS.map((step, i) => (
            <li key={step.key} className="flex items-start gap-3.5">
              <span
                className={[
                  'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[12px] font-bold',
                  step.paid
                    ? 'bg-good-soft text-good'
                    : 'bg-paper-2 text-muted-2',
                ].join(' ')}
              >
                {i + 1}
              </span>
              <div className="min-w-0">
                <h4 className="m-0 text-[14.5px] font-extrabold leading-snug text-ink-strong">
                  {t(`easypay.how_paid_${step.key}_title`)}
                </h4>
                <p className="m-0 mt-0.5 text-[13px] leading-[1.5] text-muted">
                  {t(`easypay.how_paid_${step.key}_body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <PaidExample />
      </div>
    </Modal>
  );
};
